import type { ProcessedToolkit } from "@arcadeai/ui-kit/arcade/arcade";
import { useCallback, useMemo, useState } from "react";
import { ListLayout } from "@/components/custom/list-layout/list-layout";
import { useTableViewOptions } from "@/components/custom/table/hooks/use-table-view-options";
import { useToolkitPickerData } from "@/hooks/use-toolkit-picker-data";
import { ToolkitCard } from "./components/toolkit-card";
import { ToolsEmptyState } from "./components/tools-empty-state";
import { columns } from "./data-table/columns";
import { ToolsFilters } from "./data-table/tools-filters";
import { ToolsTable } from "./data-table/tools-table";
import { useToolsSearchParams } from "./hooks/use-tools-search-params";
import { getSearchTerms, matchesSearch } from "./lib/search-utils";
import type { Tool } from "./types";

export const ToolsList = () => {
  const { data: toolkitCollection } = useToolkitPickerData();
  const {
    columnVisibility,
    setColumnVisibility,
    component: ViewOptionsComponent,
  } = useTableViewOptions({
    columns,
    storageKey: "tools-table-column-visibility",
    label: "Columns",
    className: "h-9 px-3 gap-2",
    hideColumns: ["authProviders", "secrets"],
  });

  const allTools = useMemo((): Tool[] => {
    if (!toolkitCollection?.tools) {
      return [];
    }
    return toolkitCollection.tools;
  }, [toolkitCollection?.tools]);

  // Get URL params - urlSearch is used as initial value only
  const {
    search: urlSearch,
    toolkits,
    authProviders,
    secrets,
  } = useToolsSearchParams();

  // Initialize with URL search value, then manage locally
  const [instantSearch, setInstantSearch] = useState(urlSearch);
  const [expandedToolkit, setExpandedToolkit] = useState<string | null>(null);

  const handleSearchChange = useCallback((value: string) => {
    setInstantSearch(value);
  }, []);

  const handleToggle = useCallback((toolkitName: string) => {
    setExpandedToolkit((current) =>
      current === toolkitName ? null : toolkitName
    );
  }, []);

  // Use instantSearch for filtering (client-side only, URL is just for persistence)
  const filteredTools = useMemo(() => {
    let filtered = allTools;

    if (instantSearch) {
      const terms = getSearchTerms(instantSearch);
      filtered = filtered.filter((tool) => {
        const name = tool.name || "";
        const toolkitName = tool.toolkit?.name || "";
        return matchesSearch(name, toolkitName, terms);
      });
    }

    if (toolkits.length > 0) {
      filtered = filtered.filter((tool) => {
        const toolkitName = tool.toolkit?.name;
        return toolkitName && toolkits.includes(toolkitName);
      });
    }

    if (authProviders.length > 0) {
      filtered = filtered.filter((tool) => {
        const providerId = tool.requirements?.authorization?.provider_id;
        return providerId && authProviders.includes(providerId);
      });
    }

    if (secrets.length > 0) {
      filtered = filtered.filter((tool) => {
        const toolSecrets = tool.requirements?.secrets ?? [];
        return toolSecrets.some((secret: { key: string }) =>
          secrets.includes(secret.key)
        );
      });
    }

    return filtered;
  }, [allTools, instantSearch, toolkits, authProviders, secrets]);

  const toolkitGroups = useMemo(() => {
    if (!toolkitCollection?.toolkitMap) {
      return [];
    }

    const grouped = new Map<string, Tool[]>();

    for (const tool of filteredTools) {
      const toolkitName = tool.toolkit?.name ?? "Unknown";
      const toolsArray = grouped.get(toolkitName) ?? [];
      toolsArray.push(tool);
      grouped.set(toolkitName, toolsArray);
    }

    return [...grouped.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, tools]) => ({
        // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
        toolkit: toolkitCollection.toolkitMap.get(name) as ProcessedToolkit,
        tools,
      }))
      .filter((group) => group.toolkit);
  }, [filteredTools, toolkitCollection?.toolkitMap]);

  return (
    <ListLayout
      description="List of tools that are available to use."
      filters={
        <ToolsFilters
          allTools={allTools}
          onSearchChange={handleSearchChange}
          searchValue={instantSearch}
          viewOptions={ViewOptionsComponent}
        />
      }
      title="Tools"
    >
      {toolkitGroups.length > 0 ? (
        <div className="flex flex-col gap-4">
          {toolkitGroups.map(({ toolkit, tools }) => (
            <ToolkitCard
              isExpanded={expandedToolkit === toolkit.name}
              key={toolkit.name}
              onToggle={() => handleToggle(toolkit.name)}
              toolkit={toolkit}
              toolsCount={tools.length}
            >
              <ToolsTable
                columns={columns}
                columnVisibility={columnVisibility}
                data={tools}
                hideToolkitColumn
                onColumnVisibilityChange={setColumnVisibility}
                stickyColumnBg="card"
              />
            </ToolkitCard>
          ))}
        </div>
      ) : (
        <ToolsEmptyState
          hasFilters={
            instantSearch.length > 0 ||
            toolkits.length > 0 ||
            authProviders.length > 0 ||
            secrets.length > 0
          }
        />
      )}
    </ListLayout>
  );
};
